import { Link } from "react-router-dom";
import { CalendarCheck, CalendarDays, ListChecks, Star, Clock3, ExternalLink } from "lucide-react";
import { formatTime, imageUrl } from "@/lib/format";
import { StatusBadge, EmptyState, DemoBadge } from "@/components/common";
import ProviderBookings from "@/components/provider/ProviderBookings";

const STATUS_MSG = {
  pending: ["bg-amber-50 border-amber-200 text-amber-800", "Your business profile has been submitted for review. It will appear publicly once approved."],
  rejected: ["bg-rose-50 border-rose-200 text-rose-800", "Your application was not approved. Update your details and resubmit from List Your Business."],
  suspended: ["bg-slate-100 border-slate-300 text-slate-700", "Your business is currently suspended and hidden from customers."],
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <Icon className="h-5 w-5 text-sky-600" />
    <p className="mt-3 font-heading text-3xl font-bold text-slate-900">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export function DashboardHeader({ p }) {
  const msg = STATUS_MSG[p.status];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={imageUrl(p.profile_image)} alt="" className="h-14 w-14 rounded-xl bg-slate-100 object-cover" />
          <div>
            <div className="flex items-center gap-2"><StatusBadge status={p.status} testId="provider-status" />{p.is_demo && <DemoBadge />}</div>
            <h1 className="mt-1 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">{p.business_name}</h1>
          </div>
        </div>
        <Link to={`/provider/${p.id}`} data-testid="view-public-profile" className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700"><ExternalLink className="h-4 w-4" />View profile</Link>
      </div>
      {msg && <div className={`mt-5 rounded-xl border px-4 py-3 text-sm ${msg[0]}`} data-testid="provider-status-banner">{msg[1]}</div>}
    </>
  );
}

export function StatsGrid({ stats }) {
  if (!stats) return null;
  return (
    <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
      <Stat icon={Clock3} label="Today's bookings" value={stats.today} testId="stat-today" />
      <Stat icon={CalendarDays} label="Upcoming" value={stats.upcoming} testId="stat-upcoming" />
      <Stat icon={ListChecks} label="Pending confirmation" value={stats.pending} testId="stat-pending" />
      <Stat icon={CalendarCheck} label="Total bookings" value={stats.total} testId="stat-total" />
      <Stat icon={Star} label={`${stats.review_count} reviews`} value={stats.rating_avg ? stats.rating_avg.toFixed(1) : "-"} testId="stat-rating" />
    </div>
  );
}

function TodaySchedule({ todays }) {
  if (todays.length === 0) return <EmptyState icon={CalendarDays} title="Nothing scheduled today" testId="today-empty" />;
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="today-list">
      {todays.map((b) => (
        <li key={b.id} className="flex items-center justify-between gap-3 px-5 py-4">
          <div><p className="font-semibold">{formatTime(b.start_time)} · {b.service_name}</p><p className="text-sm text-slate-500">{b.customer_name}</p></div>
          <StatusBadge status={b.status} />
        </li>
      ))}
    </ul>
  );
}

export function OverviewTab({ todays, bookings, reload, today }) {
  return (
    <>
      <h2 className="mb-4 font-heading text-xl font-semibold">Today's schedule</h2>
      <TodaySchedule todays={todays} />
      <h2 className="mb-4 mt-8 font-heading text-xl font-semibold">Upcoming bookings</h2>
      <ProviderBookings bookings={bookings} reload={reload} today={today} />
    </>
  );
}

export { ProviderBookings };
